import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Box, Button, Divider, Typography, useMediaQuery, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import CONFIG from "@APP/config";
import { ICONS } from "@APP/assets";
import { API } from "@APP/services";
import { SCREEN_PATHS } from "@APP/navigation";
import { configureLogUser, logData } from "@APP/utils";
import { PAYMENT_PROVIDER, RTPStatus } from "@APP/types";
import { PageLayout, Message, ErrorMessage, RTPSummary } from "@APP/components";
import { useAppDispatch, useAppSelector, useThunkDispatch } from "@APP/redux";
import {
  /* loadAuthToken, */ loadBanks,
  clearRtpState,
  getRtpDetails,
  setOrgLogo as setOrgLogoAction,
} from "@APP/redux/actions";
import { DESKTOP_CTA_SX } from "@APP/styles/cta";
import { MOBILE_CTA_HEIGHT, MOBILE_FONT, MOBILE_ICON } from "@APP/styles/mobile";

interface QueryParams {
  senderDataKey: string;
  rtpKey: string;
}

const RTP_UNAVAILABLE_STATUSES = [
  RTPStatus.Cancelled,
  RTPStatus.Declined,
  RTPStatus.PaymentReceived,
  RTPStatus.PaymentSent,
];

const RTP_DELETED_STATUSES = [RTPStatus.Deleted];

const useStyles = makeStyles((theme) => ({
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
    marginTop: theme.spacing(2),
  },
  cta: {
    minHeight: MOBILE_CTA_HEIGHT,
  },
}));

const LandingScreen: React.FC<RouteComponentProps> = ({ match, history }) => {
  const { t } = useTranslation();
  const dispatchThunk = useThunkDispatch();
  const dispatch = useAppDispatch();
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const rtpState = useAppSelector((state) => state.rtp);
  const banksState = useAppSelector((state) => state.banks);

  /**
   * Every way this screen can fail renders the one generic LBG error state, so
   * only whether it failed is tracked — the headline, copy and Close action all
   * come from `ErrorMessage`.
   */
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(true);

  const params = match.params as QueryParams;
  const { senderDataKey, rtpKey } = params;

  const lastSelectedBank = banksState.selectedBank;

  useEffect(() => {
    const processDataLoading = async () => {
      setLoading(true);
      if (!(senderDataKey && rtpKey)) {
        setHasError(true);
        setLoading(false);
        return;
      }
      /**
       * A different payment request means the persisted session - the request
       * details and the previously chosen bank - belongs to an earlier journey
       * and must not carry over to this one (LLOYD-392).
       */
      if (rtpState.rtpKey !== rtpKey) {
        dispatch(clearRtpState());
      }
      try {
        /**
         * Disabled due to: https://bankifi.atlassian.net/browse/BK-46
         */
        // await dispatchThunk(loadAuthToken());
        /**
         * Both Moneyhub and OpenBanking journeys pick a bank from the
         * custodians list, so both load it here.
         */
        if (
          CONFIG.PAYMENT_PROVIDER === PAYMENT_PROVIDER.MONEYHUB ||
          CONFIG.PAYMENT_PROVIDER === PAYMENT_PROVIDER.OPENBANKING
        ) {
          await dispatchThunk(loadBanks());
        }
        const rtpDetailsResult = await dispatchThunk(getRtpDetails(senderDataKey, rtpKey));

        // Use the result from the thunk instead of relying on rtpState which might not be updated yet
        const rtpData = rtpDetailsResult || rtpState;
        await configureLogUser(rtpData);

        try {
          if (!rtpData.senderData?.companyInfo?.id) {
            // The header avatar falls back to the merchant's initials when
            // there is no organisation logo.
            dispatch(setOrgLogoAction(null));
          } else {
            const res = await API.getOrganizationLogo(rtpData.senderData.companyInfo.id);
            dispatch(setOrgLogoAction(res ?? null));
          }
        } catch (error) {
          // Ignore error — the header avatar falls back to the initials.
          dispatch(setOrgLogoAction(null));
        }
      } catch (err: any) {
        // Every failure - a 5xx, errorCode 6970, or anything else - lands on
        // the same generic error state in the LBG designs.
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };
    processDataLoading();
  }, []);

  const handlePayClick = async () => {
    // -- Maverick integration --
    if (CONFIG.PAYMENT_PROVIDER === PAYMENT_PROVIDER.MAVERICK) {
      try {
        await logData("Pay Button Clicked", rtpKey, 0);
        await API.initiateRTP(rtpState.rtpData?.paymentRequestId!);

        history.push(SCREEN_PATHS.MAVERICK_PAYMENT);
      } catch (error) {
        setHasError(true);
      }

      return;
    }

    await logData("Pay Button Clicked", rtpKey, 0);
    // -- Moneyhub integration --
    if (lastSelectedBank) {
      history.push(SCREEN_PATHS.INITIATE_PAYMENT.replace(":bankId", lastSelectedBank));
      return;
    }
    history.push(SCREEN_PATHS.BANK_SELECT);
  };

  const handleDeclineClick = async () => {
    await logData("Decline Button Clicked", rtpKey, 0);
    history.push(
      SCREEN_PATHS.DECLINE_RTP.replace(":requestId", rtpState.rtpData?.paymentRequestId ?? ""),
    );
  };

  // The payer reaches an unavailable link from their bank's app or an email
  // client, so there is nowhere left to go in the journey — the only action is
  // to close the tab.
  const handleClose = () => {
    window.close();
  };

  // Shared by both "link isn't available" states below.
  const closeButton = {
    text: "Close",
    variant: "contained" as const,
    color: "primary" as const,
    onClick: handleClose,
    sx: isMobile ? { marginTop: "16px" } : DESKTOP_CTA_SX,
  };

  const renderMainContent = () => {
    if (hasError) {
      return <ErrorMessage />;
    }

    if (rtpState.rtp?.status && RTP_UNAVAILABLE_STATUSES.includes(rtpState.rtp.status)) {
      return (
        <Message
          style={{ margin: "auto" }}
          type="success"
          title="This link isn't available"
          iconSrc={ICONS.CONFIRM_ICON}
          iconSize={isMobile ? MOBILE_ICON.success : undefined}
          description={t("Screens.Landing.error1")}
          buttons={[closeButton]}
          actionsGap={0}
        />
      );
    }

    if (rtpState.rtp?.status && RTP_DELETED_STATUSES.includes(rtpState.rtp.status)) {
      return (
        <Message
          style={{ margin: "auto" }}
          type="success"
          title="This link isn't available"
          iconSrc={ICONS.CONFIRM_ICON}
          iconSize={isMobile ? MOBILE_ICON.success : undefined}
          description={t("Screens.Landing.rtpDeletedDescription")}
          buttons={[closeButton]}
          actionsGap={0}
        />
      );
    }

    return (
      <>
        <Box width="100%" mb={isMobile ? "24px" : "36px"}>
          <Typography
            component="h2"
            variant="h4"
            sx={{ fontSize: { xs: `${MOBILE_FONT.headline}px`, sm: "30px" }, lineHeight: 1.2 }}
            id="landingScreenTypo">
            Payment request
          </Typography>
        </Box>
        {/* On desktop the Figma keeps the summary cards to a narrower column,
            left-aligned under the title, while the title, divider and actions
            still span the full content width. On mobile they fill the width. */}
        <Box width="100%" maxWidth={{ xs: "none", sm: 700 }} mr={{ sm: "auto" }}>
          <RTPSummary rtpState={rtpState} />
        </Box>
        <Divider
          sx={{
            width: "100%",
            mt: isMobile ? "60px" : "56px",
            mb: isMobile ? "16px" : "24px",
          }}
        />
        {isMobile ? (
          <Box className={styles.actions}>
            <Button
              fullWidth
              color="primary"
              className={styles.cta}
              id="landingScreenPayButton"
              variant="contained"
              onClick={handlePayClick}
              sx={{ position: "relative" }}>
              Pay
              {/* The design centres the label and pins the arrow to the
                  button's right edge, so it sits apart from the text. */}
              <ArrowForwardIcon
                fontSize="small"
                sx={{
                  position: "absolute",
                  right: 20,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Button>
            <Button
              fullWidth
              className={styles.cta}
              id="landingScreenDecline"
              variant="outlined"
              color="secondary"
              onClick={handleDeclineClick}>
              Decline
            </Button>
          </Box>
        ) : (
          // The desktop design right-aligns the actions, Decline then Pay.
          <Box display="flex" justifyContent="flex-end" gap={2} width="100%">
            <Button
              sx={DESKTOP_CTA_SX}
              id="landingScreenDecline"
              variant="outlined"
              color="secondary"
              onClick={handleDeclineClick}>
              Decline
            </Button>
            <Button
              sx={DESKTOP_CTA_SX}
              color="primary"
              id="landingScreenPayButton"
              variant="contained"
              endIcon={<ArrowForwardIcon fontSize="small" />}
              onClick={handlePayClick}>
              Pay
            </Button>
          </Box>
        )}
      </>
    );
  };

  // The "link expired" states are terminal, so they take the minimal footer
  // from the designs rather than the journey link bar.
  const isLinkExpired =
    !!rtpState.rtp?.status &&
    (RTP_UNAVAILABLE_STATUSES.includes(rtpState.rtp.status) ||
      RTP_DELETED_STATUSES.includes(rtpState.rtp.status));

  // The header shows on desktop for every state. On mobile it is dropped only
  // for the "link expired" terminal state; the journey, its loading state and
  // the error state keep it, matching the approved designs.
  const hideHeader = isMobile && !loading && isLinkExpired;

  return (
    <PageLayout
      loading={loading}
      hideHeader={hideHeader}
      footerVariant={isLinkExpired ? "minimal" : "links"}>
      <Box
        display="flex"
        flex={1}
        alignItems={isMobile ? "stretch" : "center"}
        flexDirection="column"
        id="landingScreenMainBox">
        {renderMainContent()}
      </Box>
    </PageLayout>
  );
};

export default LandingScreen;
